// ShakaPlayer.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withScope, captureException, addBreadcrumb } from '@sentry/browser';
import isEqual from 'lodash/isEqual.js';

import { Severity } from '../resource/sentry.js';
import {
  EVENT_MAP,
  BUFFERING_GOAL_SECONDS,
  PRELOAD_BUFFERING_GOAL_SECONDS,
  formatSource,
  getPlaybackTuning,
} from '../resource/playerUtils.js';
import { LoadMode } from '../resource/playerConfig.js';
import { player as playerDebug } from '../resource/debug.js';
import { DrmFeatureName } from '../resource/drmConstants.js';
import { PlayerContext } from '../context/PlayerContext.js';
import playerPool from '../resource/playerPool.js';
import {
  SHAKA_DEFAULT_BANDWIDTH_ESTIMATE,
  checkIfBrowserSupported,
  createFreshAbrManagerFactory,
  updateShakaPlayerConfiguration,
  registerShakaPlayerDrmRequestFilter,
} from '../resource/shakaFactory.js';
import { getIsOnApple } from '../resource/getUserAgent.js';
import createAesEmbeddedManifestDataUrl from '../resource/createAesEmbeddedManifestDataUrl.js';
import trackEvent from '../resource/mixpanel.js';
import { EventTypes } from '../resource/trackingConstants.js';

const playerDebugLog = playerDebug.extend('log:ShakaPlayer');
const playerError = playerDebug.extend('error:ShakaPlayer');
const playerLog = ({ messages, data }) => {
  addBreadcrumb({
    category: 'player',
    level: Severity.Info,
    message: messages.join(' '),
    data,
  });
  playerDebugLog(...messages, data);
};

// Store handler names (the handlers created in this component)
const mainEventHandlers = Object.create(null);
// Store handlers (the handlers passed via props)
const extraEventHandlers = Object.create(null);

const mainVideoEvents = [
  'loadedmetadata',
  'play',
  'pause',
  'ended',
  // 'error',
];
mainVideoEvents.forEach(event => {
  const capitalizedEvent = event.charAt(0).toUpperCase() + event.slice(1);
  mainEventHandlers[event] = `handle${capitalizedEvent}`;
});

// What `componentDidUpdate` records in its `diff` when a short becomes the
// active one and nothing that feeds its source changed alongside.
const ACTIVE_EDGE_DIFF_KEYS = ['isActiveVideo', 'prevIsActiveVideo'];

// This is for debugging purpose.
// It helps us figure out how many requests are still waiting,
// and which one has required resource successfully.
const ACQUISITION_HISTORY_SIZE = 30;
const acquisitionHistory = new Map();
if (typeof window !== 'undefined') {
  window.acquisitionHistory = acquisitionHistory;
}

const getDebugNo = () => {
  let num = Math.floor(Math.random() * 1000);
  num = num.toString();
  while (num.length < 3) num = '0' + num; // pad
  return num;
};

const getLogSafePath = url => {
  if (typeof url !== 'string') return url;
  if (url.startsWith('data:')) {
    return `${url.slice(0, 64)}…(${url.length} chars)`;
  }
  if (url.startsWith('blob:')) return url;
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch (_) {
    return url;
  }
};

class ShakaPlayer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.videoContainerRef = React.createRef();
    this._player = null;
  }

  setPlayer = () => undefined;

  captureException = error => {
    withScope(scope => {
      scope.setFingerprint(['shaka-player-error', error?.code]);

      // Additional Data
      scope.setExtra('source', this.props.source);
      scope.setExtra('drmFeatureName', this.props.drmFeatureName);
      scope.setExtra('key system (passed from props)', this.props.keySystem);
      const player = this._player;
      if (player) {
        scope.setExtra('key system (used by Shaka)', player.keySystem());
        scope.setExtra('isPaused', player.paused());
        scope.setExtra('duration', player.duration());
        scope.setExtra('currentSrc', player.currentSrc());
        scope.setExtra('active player No.', player.serialNumber);
        const videoElement = player.getMediaElement?.();
        scope.setExtra('eme_state', {
          hasMediaKeys: !!videoElement?.mediaKeys,
          mediaKeysKeySystem: videoElement?.mediaKeys?.keySystem,
          videoReadyState: videoElement?.readyState,
          videoNetworkState: videoElement?.networkState,
          drmInfo: player.drmInfo?.() || null,
        });
      } else {
        scope.setExtra('active player No.', 'N/A');
      }
      scope.setExtra('error', error);
      scope.setExtra(
        'acquisitionHistory',
        Object.fromEntries(acquisitionHistory.entries())
      );

      // Sample error object on Sentry:
      /*
      {
        category: 3, 
        code: 3015, 
        data: [ [DOMException] ], 
        handled: False, 
        severity: 2
      }
      */
      let errorMessage = error?.message || '';
      let domException;
      if (Array.isArray(error?.data)) {
        const flattened = error.data.flatMap(el => el);
        domException = flattened.find(el => el instanceof DOMException);
      }
      if (domException) {
        errorMessage = domException.message || '';
        scope.setExtra('DOMExceptionCode', domException.code);
        scope.setExtra('DOMExceptionMessage', domException.message);
        scope.setExtra('DOMExceptionName', domException.name);
        scope.setExtra('DOMException', domException);
      }
      captureException(
        new Error(`SHAKA-PLAYER CODE "${error?.code}" ${errorMessage}`)
      );
    });
  };

  /**
   * Add necessary event handlers to the video element
   */
  bindListeners = ({ debugNo, player }) => {
    const detailedLogMessage =
      `(debugNo ${debugNo})(Player ${player.serialNumber})` +
      (player.playlistCount
        ? `(playback order: ${player.playbackOrder} / ${player.playlistCount})`
        : '(single source; no playlist)') +
      ' bindListeners';
    playerLog({
      messages: [detailedLogMessage, 'init'],
    });

    // The handlers that are created by this component.
    mainVideoEvents.forEach(eventName => {
      player
        .getMediaElement()
        .addEventListener(eventName, this[mainEventHandlers[eventName]]);
    });
    playerLog({
      messages: [detailedLogMessage, 'main events bound'],
    });

    // The handlers that are passed via props.
    Object.keys(EVENT_MAP).forEach(key => {
      const eventName = EVENT_MAP[key];
      const logTag = key.slice(2).toLowerCase();
      const callback = this.props[key];
      if (typeof callback === 'function') {
        const eventHandler = event => {
          callback(event, player);
          if (!['progress', 'timeupdate'].includes(logTag)) {
            playerLog({
              messages: [
                `(debugNo ${debugNo})(Player ${player.serialNumber})` +
                  (player.playlistCount
                    ? `(playback order: ${player.playbackOrder} / ${player.playlistCount})`
                    : '(single source; no playlist)'),
                logTag,
                'fired',
              ],
              data: { target: event.target },
            });
          }
        };

        // keep corresponding handlers for multiple players
        if (extraEventHandlers[eventName]) {
          extraEventHandlers[eventName].push({ player, eventHandler });
        } else {
          extraEventHandlers[eventName] = [{ player, eventHandler }];
        }

        player.getMediaElement().addEventListener(eventName, eventHandler);
      }
    });

    playerLog({
      messages: [
        detailedLogMessage,
        `extra events (${Object.keys(EVENT_MAP).join()}) bound`,
      ],
    });

    player.addEventListener('error', this.handlerPlayerError);
    player.addEventListener('buffering', this.handleBuffering);

    const shakaEvents = ['manifestparsed', 'stalldetected'];
    player._debugShakaHandlers = shakaEvents.reduce((acc, name) => {
      const handler = () => {
        const data = {};
        if (name === 'manifestparsed') {
          const variants = player.getVariantTracks?.() || [];
          const v = variants.find(t => t.active) || variants[0];
          data.variantCount = variants.length;
          data.mimeType = v?.mimeType;
          data.codecs = v?.codecs;
          data.videoCodec = v?.videoCodec;
          data.audioCodec = v?.audioCodec;
        }
        playerLog({
          messages: [`(Player ${player.serialNumber}) [Shaka] ${name}`],
          data,
        });
      };
      player.addEventListener(name, handler);
      acc[name] = handler;
      return acc;
    }, {});

    const videoElement = player.getMediaElement?.();
    if (videoElement) {
      const videoEvents = ['waiting', 'stalled', 'error'];
      player._debugVideoHandlers = videoEvents.reduce((acc, name) => {
        const handler = () => {
          playerLog({
            messages: [`(Player ${player.serialNumber}) [video] ${name}`],
            data: {
              readyState: videoElement.readyState,
              networkState: videoElement.networkState,
              currentTime: videoElement.currentTime,
              errorCode: videoElement.error?.code,
              errorMsg: videoElement.error?.message,
            },
          });
        };
        videoElement.addEventListener(name, handler);
        acc[name] = handler;
        return acc;
      }, {});
    }

    return player;
  };

  /**
   * Get the sources of trailer, splash, or main content
   */
  getPlaylist({ debugNo, source }) {
    const {
      shouldUseTrailer,
      shouldCombineTrailer,
      shouldUseSplash,
      splashVideo,
      contentUrl,
      isAesEncrypted,
    } = this.props;

    playerLog({
      messages: [`(debugNo ${debugNo}) getPlaylist`],
      data: {
        source,
        props: {
          shouldUseTrailer,
          shouldCombineTrailer,
          shouldUseSplash,
          contentUrl,
        },
      },
    });

    const sources = formatSource({
      dash: source.dash,
      hls: source.hls,
    });

    // AES encrypted post without blurred preview, use trailer instead
    if (isAesEncrypted && !sources.length && source.mp4) {
      const trailer = formatSource({ mp4: source.mp4 });
      return [trailer[0].src];
    }

    // If `ShakaPlayer/MessageVideoPlayer` is used alone, `sources[0].src` would be applied.
    // `contentUrl`, by contrast, only available if `ShakaPlayer/MessageVideoPlayer` is wrapped by `MediaSourceWrapper`.
    const contentSource = contentUrl || sources[0].src;
    if (shouldUseTrailer) {
      const trailer = formatSource({ mp4: source.mp4 });
      return [trailer[0].src];
    } else if (shouldCombineTrailer) {
      const trailer = formatSource({ mp4: source.mp4 });
      return [trailer[0].src, contentSource];
    } else if (shouldUseSplash) {
      const splash = formatSource(splashVideo);
      if (splash.length) {
        return [splash[0].src, contentSource];
      }
    }

    return [contentSource];
  }

  /**
   * Notify the other components that there is a new active player
   */
  setActivePlayer(player, debugNo) {
    const detailedLogMessage =
      `(debugNo ${debugNo})(Player ${player.serialNumber})` +
      (player.playlistCount
        ? `(playback order: ${player.playbackOrder} / ${player.playlistCount})`
        : '(single source; no playlist)') +
      ' setActivePlayer';
    this.props.onSetup(player);
    this.setPlayer(player);
    player.triggerReady();
    playerLog({
      messages: [
        detailedLogMessage,
        `Player ${player.serialNumber} has activated`,
      ],
    });
  }

  reload(player) {
    // Not sure if the thumbnail should be kept,
    // but video.js doesn't display thumbnail when reloading the same source.
    // Also, since Safari always display first frame,
    // let's remove thumbnail first to avoid:
    // poster -> first frame -> video
    player.getMediaElement().removeAttribute('poster');

    player.isReloadRequired = false;

    playerLog({
      messages: ['reload', 'start'],
      data: {
        src: player.getAssetUri(),
        currentTime: player.currentTime(),
      },
    });
    const assetUri = player.getAssetUri();
    const isBlobUri = assetUri.includes('blob:');
    player
      .load(assetUri, player.currentTime(), isBlobUri ? 'video/mp4' : undefined)
      .then(() => {
        this.setReloadTimer(player);
        playerLog({ messages: ['reload', 'end'] });
      });
  }

  deactivatePlayer({ player, debugNo, reason }) {
    if (!player) {
      playerLog({
        messages: [
          `(debug ${debugNo}) deactivatePlayer (${reason})`,
          'early return due to no player found',
        ],
      });
      return;
    }

    // Whatever this player had loaded is about to go away, and `unload()` is
    // asynchronous — releasePlayer only clears this once it resolves. Until
    // then a swipe back would see a player that still looks preloaded and
    // `startPreloadedPlayer` would play one that is mid-unload.
    if (this._loadedPlayer === player) {
      this._loadedPlayer = undefined;
    }

    const videoElement = player.getMediaElement();
    const timestamp = Date.now() + ''; // convert it to string
    player.videoElementDeletedTime = timestamp;
    videoElement.dataset.deletedTime = timestamp; // string in essence

    window.clearTimeout(player.reloadTimer);
    player.isReloadRequired = undefined;
    player.reloadTimer = undefined;

    if (player._endedHandler) {
      videoElement.removeEventListener('ended', player._endedHandler);
    }
    this.unbindListeners(player, debugNo);

    if (!player.getAssetUri()) {
      // In this case, player.unload() would be unresponsive (neither resolve nor reject).
      playerLog({
        messages: [
          `(debug ${debugNo})(Player ${player.serialNumber}) deactivatePlayer`,
          'unbound but not to unload (due to no assetUri)',
        ],
      });
      player._isReady = false;
      player._readyQueue = [];
      this.releasePlayer({
        debugNo,
        player,
        reason: 'deactivating the player',
      });
      return;
    }
    player
      .unload()
      .then(() => {
        playerLog({
          messages: [
            `(debug ${debugNo})(Player ${player.serialNumber}) deactivatePlayer`,
            'unloaded & unbound',
          ],
        });
        player._isReady = false;
        player._readyQueue = [];
        this.releasePlayer({
          debugNo,
          player,
          reason: 'deactivating the player',
        });
      })
      .catch(err => {
        // We haven't encountered any cases that would cause errors.
        // But let's add this just in case.
        playerError(
          `(debugNo: ${debugNo})(Player ${player.serialNumber}) deactivatePlayer - failed to unload`,
          err
        );
        player._isReady = false;
        player._readyQueue = [];
        this.releasePlayer({
          debugNo,
          player,
          reason: 'deactivating the player',
        });
      });
  }

  releasePlayer = ({ debugNo, player, reason }) => {
    playerLog({
      messages: [
        `(debugNo: ${debugNo}) releasePlayer`,
        `about to release it due to: ${reason}`,
      ],
      data: playerPool.getStatus(),
    });

    if (this._player) {
      this._player = undefined;
      // The pool hands the same instance back out, so these have to be cleared
      // or the next acquisition would look like it was already announced, and an
      // unsettled load from before the release could shield the fresh one.
      this._announcedPlayer = undefined;
      this._loadingSource = undefined;
      this._loadedPlayer = undefined;
      // Only clear the debug handle if it is still pointing at this player;
      // a preloaded one being released must not wipe the active short's.
      if (
        typeof window !== 'undefined' &&
        window.activeShakaPlayer === player
      ) {
        window.activeShakaPlayer = undefined;
      }
    } else {
      playerLog({
        messages: [
          `(debugNo: ${debugNo}) releasePlayer`,
          'early return b/c this._player has been reset',
        ],
        data: playerPool.getStatus(),
      });
      return;
    }

    // `acquisitionHistory` is just for debugging.
    // It keeps track of the status of every requests.
    const acquiringTimestamp = player.acquiringTimestamp;
    acquisitionHistory
      .get(acquiringTimestamp)
      .acquisitionStatus.unshift(`releasing due to ${reason}`);
    playerPool.releasePlayer(player).then(() => {
      playerLog({
        messages: [
          `(debug ${debugNo}) releasePlayer`,
          `player released (${player._lastAcquiringTimestamp || ''})`,
        ],
        data: playerPool.getStatus(),
      });

      // It's necessary for PostAssetFullscreenModal.
      // (_isPlayerInModalCreated should be reset to bring back the player covered by modal)
      this.resetReleasingFlags();
      acquisitionHistory
        .get(acquiringTimestamp)
        .acquisitionStatus.unshift('released');
    });
  };

  unbindListeners(player, debugNo) {
    const detailedLogMessage = `(debug ${debugNo})(Player ${player.serialNumber}) unbindListeners`;
    playerLog({
      messages: [detailedLogMessage, 'init'],
    });

    player.removeEventListener('error', this.handlerPlayerError);
    player.removeEventListener('buffering', this.handleBuffering);

    if (player._debugShakaHandlers) {
      for (const [name, handler] of Object.entries(
        player._debugShakaHandlers
      )) {
        player.removeEventListener(name, handler);
      }
      player._debugShakaHandlers = null;
    }
    const videoElement = player.getMediaElement?.();
    if (videoElement && player._debugVideoHandlers) {
      for (const [name, handler] of Object.entries(
        player._debugVideoHandlers
      )) {
        videoElement.removeEventListener(name, handler);
      }
      player._debugVideoHandlers = null;
    }

    mainVideoEvents.forEach(eventName => {
      player
        .getMediaElement()
        .removeEventListener(eventName, this[mainEventHandlers[eventName]]);
    });
    playerLog({ messages: [detailedLogMessage, 'main events unbound'] });

    Object.keys(EVENT_MAP).forEach(key => {
      const eventName = EVENT_MAP[key];
      const callback = this.props[key];
      if (typeof callback === 'function') {
        const extraEventHandler = extraEventHandlers[eventName];
        const handlerIndex = extraEventHandler
          .map(handlerEntry => handlerEntry.player)
          .indexOf(player);
        if (handlerIndex >= 0) {
          const handlerEntry = extraEventHandler.splice(handlerIndex, 1)[0];
          player
            .getMediaElement()
            .removeEventListener(eventName, handlerEntry.eventHandler);
        } else {
          playerLog({
            messages: [
              detailedLogMessage,
              'no extra handlers found as unbinding',
            ],
          });
        }
      }
    });

    if (player._endedHandler) {
      player
        .getMediaElement()
        .removeEventListener('ended', player._endedHandler);
      player._endedHandler = undefined;
    }

    playerLog({ messages: [detailedLogMessage, 'extra events unbound'] });

    // Just in case there are still listeners left with an inactive player,
    // especially for those are not controlled by ShakaPlayer.jsx,
    // e.g., VideoWatermark.jsx, FlixPlayerNoSeekDetector.js
    player.off();
    playerLog({
      messages: [detailedLogMessage, 'events bound with ON are unbound'],
    });

    // fullscreen related
    window.removeEventListener(
      'orientationchange',
      player.rotationchangeHandler
    );
    document.removeEventListener(
      'fullscreenchange',
      player.fullscreenchangeHandler
    );
  }

  startProgressAnimation() {
    const isVideo = 0 === this.props.mediaType.search('video');
    const hasCallback = typeof this.props.onProgressUpdate === 'function';
    if (!isVideo || !hasCallback) {
      return null;
    }

    this.fpsInterval = 1000 / 12;
    this.then = Date.now();

    this.timer = window.requestAnimationFrame(this.updateProgress);
  }

  updateProgress = () => {
    this.timer = window.requestAnimationFrame(this.updateProgress);

    const now = Date.now();
    const elapsed = now - this.then;

    if (elapsed > this.fpsInterval) {
      this.then = now - (elapsed % this.fpsInterval);

      if (this._player) {
        const currentTime = this._player.currentTime();
        const duration = this._player.duration();
        this.props.onProgressUpdate({ currentTime, duration });
      }
    }
  };

  // It's not a native video event; this comes from the player.
  handlerPlayerError = event => {
    const player = event.target;
    const error = event.detail;
    playerError(`(Player ${player.serialNumber}) Errors from Shaka:`, event);
    trackEvent({
      type: EventTypes.PLAYER_ERROR,
      payload: {
        messageId: this.props.messageId,
        errorReason: 'shaka-runtime-error',
        errorCode: error?.code ?? null,
        errorCategory: error?.category ?? null,
        errorData: JSON.stringify(error?.data ?? null),
        isErrorUndefined: error == null,
      },
    });
    this.captureException(error);
  };

  handleBuffering = event => {
    const player = this._player;
    const videoElement = player?.getMediaElement?.();
    let buffered = null;
    try {
      buffered = player?.getBufferedInfo?.();
    } catch (_) {
      // getBufferedInfo may throw before manifest is parsed
    }
    playerLog({
      messages: [`(Player ${player?.serialNumber}) [Shaka] buffering`],
      data: {
        buffering: event.buffering,
        readyState: videoElement?.readyState,
        currentTime: videoElement?.currentTime,
        bufferedTotal: buffered?.total,
      },
    });
  };

  handleLoadedmetadata = event => {
    playerLog({ messages: ['handleLoadedmetadata'] });

    this.props.onLoadedMetadata(event, this._player);
  };

  handlePlay = () => {
    const player = this._player;
    const { isNeedReloadCycle, onPlay } = this.props;
    playerLog({
      messages: ['handlePlay'],
      data: {
        isNeedReloadCycle,
        isReloadRequired: player.isReloadRequired,
      },
    });
    if (isNeedReloadCycle && player.isReloadRequired) {
      this.reload(player);
    }
    window.cancelAnimationFrame(this.timer);
    this.startProgressAnimation();
    onPlay();
  };

  handlePause = event => {
    playerLog({ messages: ['handlePause'], data: event });
    this.props.onPause();
    window.cancelAnimationFrame(this.timer);
  };

  handleEnded = event => {
    playerLog({ messages: ['handleEnded'], data: event });
    window.cancelAnimationFrame(this.timer);

    const player = this._player;
    if (
      player?.playlistCount &&
      player?.playlistCount !== player?.playbackOrder
    ) {
      playerLog({
        messages: [
          'handleEnded',
          'early return due to not the last video in playlist',
        ],
        data: {
          playbackOrder: player.playbackOrder,
          playlistCount: player.playlistCount,
        },
      });
      return;
    }

    this.props.onEnded();
  };

  // Some special tags need to be reset.
  // Otherwise a newly created player instance would malfunction
  resetReleasingFlags = () => {
    // If we don't clear `_unmounting`, we'll encounter an issue after we upgrade next to 14, which is:
    // In reactStrictMode, some lifecycle methods, such as componentDidMount, would run twice on purpose,
    // also, these two runs would share the same `this`,
    // in hence, the flag set by the previous run may affect the later run,
    // that's why we should clear `_unmounting` here to avoid the wrong setting for the second run in reactStrictMode.
    this._unmounting = undefined;
    this._isPostInactive = undefined;
    this._isShortInactive = undefined;
    this._isPlayerInModalCreated = undefined;
  };

  /**
   * A short that is mounted inside the preload window but is not the one being
   * watched. It still loads, but parks on its first frame and stays out of the
   * way of the short that is playing.
   */
  getIsPreloading = () =>
    this.props.drmFeatureName === DrmFeatureName.SHORT &&
    !this.props.isActiveVideo &&
    this.props.loadMode === LoadMode.PRELOAD;

  playback = debugNo => {
    const {
      messageId,
      source,
      isDrmLicenseAvailabilityFetched,
      shouldAutoplayPost,
      drmFeatureName,
      isActiveVideo,
      loadMode,
    } = this.props;
    const isPreloading = this.getIsPreloading();

    playerLog({
      messages: [`(debugNo: ${debugNo}) playback`, 'enter'],
      data: {
        messageId,
        drmFeatureName,
        isDrmLicenseAvailabilityFetched,
        isActiveVideo,
        loadMode,
        shouldAutoplayPost,
        hasExistingPlayer: !!this._player,
        hasContainer: !!this.videoContainerRef?.current,
        sourceShape: {
          dash: !!source?.dash,
          hls: !!source?.hls,
          mp4: !!source?.mp4,
        },
      },
    });

    if (!isDrmLicenseAvailabilityFetched) {
      playerLog({
        messages: [
          `(debugNo: ${debugNo}) playback`,
          'early return due to DrmLicenseAvailability not fetched yet',
        ],
        data: { messageId, drmFeatureName },
      });
      return;
    }

    // (Case 1)
    // If there is an existing player, reuse it rather than asking for a new one.
    if (this._player) {
      playerLog({
        messages: [`(debugNo: ${debugNo}) playback`, 'reuse player'],
      });

      const player = this._player;
      if (player._endedHandler) {
        player
          .getMediaElement()
          .removeEventListener('ended', player._endedHandler);
      }

      this.playWithPlaylist({ debugNo, source, player, isPlayerReused: true });
      return;
    }

    // (Case 2)
    // Ask for a new player instance from the pool
    const acquiringTimestamp = Date.now();
    this._lastAcquiringTimestamp = acquiringTimestamp;
    playerLog({
      messages: [`(debugNo: ${debugNo}) playback`, 'about to acquirePlayer'],
      data: { messageId, poolStatus: playerPool.getStatus() },
    });

    // `acquisitionHistory` is only for debugging.
    acquisitionHistory.set(acquiringTimestamp, {
      acquisitionStatus: ['waiting'],
      messageId,
      debugNo,
    });
    for (const key of acquisitionHistory.keys()) {
      if (acquisitionHistory.size > ACQUISITION_HISTORY_SIZE) {
        acquisitionHistory.delete(key);
      } else {
        break;
      }
    }
    playerPool.acquirePlayer().then(async response => {
      if (response.error) {
        trackEvent({
          type: EventTypes.PLAYER_ERROR,
          payload: {
            messageId,
            debugNo,
            errorReason: 'acquire-player-failed',
            errorCode: response.error?.code ?? null,
            errorData: JSON.stringify(response.error?.data ?? null),
            isErrorUndefined: response.error == null,
          },
        });
        playerError(`(debugNo: ${debugNo}) failed to acquire`, response.error);
        this.captureException(response.error);
        this.releasePlayer({
          debugNo,
          player: response,
          reason: 'an error occurred as acquiring',
        });
      } else {
        playerLog({
          messages: [`(debugNo: ${debugNo}) playback`, 'acquired successfully'],
          data: { messageId, poolStatus: playerPool.getStatus() },
        });
        acquisitionHistory
          .get(acquiringTimestamp)
          .acquisitionStatus.unshift('acquired');

        const player = response;
        player.debugNo = debugNo;
        // Give a player an identifier every time.
        // There might be several requests at the same time, but only the last one matters.
        player.acquiringTimestamp = acquiringTimestamp;
        // Keep it since we need to specifically tell pool which player needs to be released.
        this._player = player;
        // A preloaded player is not the one on screen; letting it claim this
        // would point debugging at the wrong short.
        if (typeof window !== 'undefined' && !isPreloading) {
          window.activeShakaPlayer = player; // for debug
        }

        // Since `acquirePlayer` is asynchronous,
        // the new operations might happen during (or before, if it's waiting) the acquisition process.
        // For example, if a user enters a page and leaves it very quickly,
        // the expected player instance may not be ready/created while the component is unmounting.
        // When the actual acquisition process running in this kind of situation,
        // it doesn't need to go further because the following process is gonna be useless.
        if (
          this._unmounting ||
          this._isPostInactive ||
          this._isShortInactive ||
          this._isPlayerInModalCreated
        ) {
          const reason = this._unmounting
            ? 'the component is unmounting'
            : this._isPlayerInModalCreated
              ? 'post detail is covered by modal'
              : this._isPostInactive
                ? 'the Post is inactive'
                : 'the Short is inactive';

          // Can't just wait playerPool.releasePlayer() to resetReleasingFlags()
          // It might be too late since playerPool.releasePlayer() is asynchronous.
          this.resetReleasingFlags();
          this.releasePlayer({ debugNo, player, reason });
          return;
        }

        // Release SHORT when it's staled — but a short that is preloading is
        // waiting its turn, not stale, and handing its player back here is
        // what would leave it with nothing loaded when the swipe arrives.
        if (
          drmFeatureName === DrmFeatureName.SHORT &&
          !isActiveVideo &&
          !isPreloading
        ) {
          this.releasePlayer({
            debugNo,
            player,
            reason: 'the SHORT is not active now',
          });
          return;
        }

        // Release POST when it's staled.
        if (drmFeatureName === DrmFeatureName.POST && !shouldAutoplayPost) {
          this.releasePlayer({
            debugNo,
            player,
            reason: 'the POST should not auto play now',
          });
          return;
        }

        // Only the last request should keep the instance.
        if (acquiringTimestamp !== this._lastAcquiringTimestamp) {
          this.releasePlayer({
            debugNo,
            player,
            reason: 'this acquirement is staled',
          });
          return;
        }

        if (!this.videoContainerRef?.current) {
          this.releasePlayer({
            debugNo,
            player,
            reason: 'no video container was found',
          });
          return;
        }

        this.videoContainerRef.current.prepend(player.getMediaElement());

        this.playWithPlaylist({
          debugNo,
          source,
          player,
          isPlayerReused: false,
        });
      }
    });
  };

  /**
   * Get a playlist,
   * notify all the components that the player is ready,
   * and then load a video.
   */
  playWithPlaylist = ({ debugNo, source, player, isPlayerReused }) => {
    const { messageId } = this.props;
    const playlist = this.getPlaylist({ debugNo, source });
    playerLog({
      messages: [`(debugNo: ${debugNo}) playWithPlaylist`, 'playlist is ready'],
      data: { playlist },
    });

    this.setPlayerParams({
      player,
      playlist,
      debugNo,
      isPlayerReused,
    });
    const autoplay = player.getMediaElement().autoplay;
    playerLog({
      messages: [
        `(debugNo: ${debugNo}) playWithPlaylist`,
        'setPlayerParams has set. about to set player to active and load source',
      ],
      data: { autoplay },
    });

    // (2024.06)
    // No need to set player again if the player is reused.
    // Otherwise, it will send redundant PLAYER_VISIBLE tracking events.
    // (We would send PLAYER_VISIBLE when the player instance is acquired by other components,
    // no matter the player instance is the same or not)
    //
    // A preloading short has no viewer yet either — announcing it here would
    // report a short as visible before anyone has swiped to it, so
    // `startPreloadedPlayer` does it at the moment that actually happens.
    // Tracking which player was announced, rather than reusing
    // `isPlayerReused`, also covers the swipe that lands while a preload is
    // still loading: that goes through the reuse path, which would otherwise
    // leave the short with no player reference at all.
    if (!this.getIsPreloading() && this._announcedPlayer !== player) {
      this._announcedPlayer = player;
      this.setActivePlayer(player, debugNo);
    }
    this.loadSource({ player, debugNo, src: playlist[0], messageId });
  };

  /**
   * Leverage this method to update required information to player instance,
   * since we can't pass any parameters through genericPool's create()
   */
  setPlayerParams = ({ player, playlist, debugNo, isPlayerReused }) => {
    const {
      token,
      clientId,
      sessionId,
      isMuted,
      option,
      messageId,
      assetId,
      keySystem,
      robustness,
      openPlaybackNotSupportAlert,
      widevineServerCertificateBase64,
      isAesEncrypted,
      drmFeatureName,
    } = this.props;
    const isPreloading = this.getIsPreloading();
    playerLog({
      messages: [`(debugNo: ${debugNo}) setPlayerParams`],
      data: { isPlayerReused, option, isPreloading },
    });
    // stick to the following order:
    // 1. Set playbackOrder, playlistCount & other information
    // 2. Bind listeners
    // 3. Set _endedHandler if needed
    const playlistCount = playlist.length;
    player.playbackOrder = playlistCount > 1 ? 1 : undefined;
    player.playlistCount = playlistCount > 1 ? playlistCount : undefined;
    player.token = token || '';
    player.clientId = clientId || '';
    player.sessionId = sessionId || '';
    player.robustness = robustness;
    player.widevineServerCertificateBase64 = widevineServerCertificateBase64;

    if (!checkIfBrowserSupported()) {
      // It only shows once in 24 hours
      openPlaybackNotSupportAlert();
    }

    if (isAesEncrypted) {
      playerLog({
        messages: ['[AES] flow selected'],
        data: { messageId, assetId },
      });
      // Apple: drop residual com.apple.fps so Safari native HLS doesn't try
      // FairPlay on an AES-128 manifest (PR #11191). Other platforms skip it
      // to avoid a second configure — see shakaFactory.js warning.
      if (getIsOnApple()) {
        player.configure({ drm: { servers: { 'com.apple.fps': undefined } } });
        player._isPlayerConfigurationSet = false;
      }
      const playbackTuning = getPlaybackTuning({ drmFeatureName });
      try {
        player.configure({
          abr: {
            // navigator.connection.downlink over-reports, which makes playback
            // open at the top tier and stall on the first segment; ignore it and
            // let measured throughput take over instead.
            useNetworkInformation: false,
            defaultBandwidthEstimate: SHAKA_DEFAULT_BANDWIDTH_ESTIMATE,
            ...playbackTuning.abrBandwidthSeed,
          },
          // A short also leaves the estimate of the video before it behind,
          // otherwise the seed above never gets consulted.
          ...(playbackTuning.shouldOpenOnLowestRendition && {
            abrFactory: createFreshAbrManagerFactory(),
          }),
          streaming: {
            rebufferingGoal: playbackTuning.rebufferingGoalSeconds,
          },
        });
      } catch (error) {
        // Quality-only config, so it must not abort setPlayerParams: throwing
        // inside acquirePlayer().then() would leak the acquired player.
        playerLog({
          messages: [`(debugNo: ${debugNo}) [AES] playback tuning FAILED`],
          data: { error: error?.message, stack: error?.stack },
        });
      }
    } else {
      playerLog({
        messages: ['[DRM] flow selected'],
        data: { messageId, assetId, isAesEncrypted },
      });
      updateShakaPlayerConfiguration({
        player,
        keySystem,
        robustness,
        drmFeatureName,
      });
      registerShakaPlayerDrmRequestFilter(player);
    }

    // A preloaded short only needs enough buffered to start instantly; the one
    // being watched keeps the full goal. Neither side is AES-specific — the
    // park and restore paths do not look at AES either.
    player.configure({
      streaming: {
        bufferingGoal: isPreloading
          ? PRELOAD_BUFFERING_GOAL_SECONDS
          : BUFFERING_GOAL_SECONDS,
      },
    });

    const videoElement = player.getMediaElement();
    videoElement.removeAttribute('poster');
    videoElement.removeAttribute('loop');
    // A preloaded short has to stop at its first frame — autoplaying it would
    // start a second short behind the one the viewer is watching.
    videoElement.autoplay = !isPreloading;
    videoElement.playsInline = true;
    if (videoElement && option) {
      for (const [key, value] of Object.entries(option)) {
        videoElement[key] = value;
      }
    }
    videoElement.muted = isMuted; // global setting; always takes precedence
    videoElement.dataset.debugNo = debugNo;
    videoElement.dataset.messageId = messageId;
    videoElement.dataset.assetId = assetId;

    if (!isPlayerReused) {
      this.bindListeners({ debugNo, player });
    }

    if (player.playlistCount) {
      player._endedHandler = () => {
        if (player.playbackOrder < player.playlistCount) {
          this.loadSource({
            player,
            debugNo,
            src: playlist[player.playbackOrder++],
            messageId,
          });
        }
      };
      player.getMediaElement().addEventListener('ended', player._endedHandler);
    }
  };

  // Build the AES data URL for a manifest, reusing an in-flight or finished
  // build of the same manifest and credentials. The chain behind it — fetch the
  // master, fetch every variant playlist, fetch and inline the key, rewrite the
  // lot — measured ~350ms, and disk cache does not shorten it because most of
  // that is work, not transfer.
  resolveAesSource = ({ manifestUrl, messageId }) => {
    const { clientId, sessionId, token } = this.props;
    // The credentials are part of the key, not just the URL: the key request
    // inside the build is authenticated with them, so a token refresh has to
    // rebuild rather than hand back a source built with the old one.
    const cacheKey = JSON.stringify([manifestUrl, clientId, sessionId, token]);

    if (this._aesSource?.cacheKey !== cacheKey) {
      const promise = createAesEmbeddedManifestDataUrl({
        manifestUrl,
        clientId,
        sessionId,
        token,
        messageId,
      });
      // A short the viewer never swipes to still settles this promise, and a
      // rejection with no handler attached would surface as an unhandled one.
      // loadSource attaches the real error handling if it ever gets used.
      promise.catch(() => {
        // Never cache a failure: a single transient error would otherwise stick
        // for the life of this component and no later playback attempt could
        // rebuild it. Drop it by promise identity so a newer build for another
        // manifest survives.
        if (this._aesSource?.promise === promise) {
          this._aesSource = undefined;
        }
      });
      this._aesSource = { cacheKey, promise };
    }

    return this._aesSource.promise;
  };

  // The payoff of preloading: this short already holds a player parked on its
  // first frame, so becoming active is just a play(). Routing it through
  // playback() instead would reach loadSource, which unloads and reloads the
  // very source that was preloaded — slower than never having preloaded.
  startPreloadedPlayer = debugNo => {
    const player = this._player;
    playerLog({
      messages: [
        `(debug ${debugNo}) startPreloadedPlayer`,
        'play the source this short already preloaded',
      ],
      data: { assetUri: getLogSafePath(player.getAssetUri()) },
    });

    if (typeof window !== 'undefined') {
      window.activeShakaPlayer = player; // for debug
    }
    player.configure({ streaming: { bufferingGoal: BUFFERING_GOAL_SECONDS } });
    // `autoplay` only takes effect at load time, so the play has to be
    // explicit; the attribute is set for any later reload of this player.
    player.getMediaElement().autoplay = true;
    this._announcedPlayer = player;
    this.setActivePlayer(player, debugNo);
    // ShortMessage reports playback failures through its own play() path.
    player.play()?.catch(() => undefined);
  };

  loadSource = ({ player, debugNo, src, messageId }) => {
    // A swipe landing while a preload is still loading takes the reuse path and
    // would ask for the same source on the same player again: shaka aborts the
    // first load with code 7000 and the swipe pays for the load twice — the very
    // cost preloading is meant to remove. Hand back the load already in flight.
    if (
      this._loadingSource?.player === player &&
      this._loadingSource?.src === src
    ) {
      playerLog({
        messages: [
          `(debugNo: ${debugNo}) loadSource`,
          'reuse the load already in flight',
        ],
        data: { messageId, serialNumber: player.serialNumber },
      });
      return this._loadingSource.promise;
    }

    playerLog({
      messages: [`(debugNo: ${debugNo}) loadSource`, 'enter'],
      data: {
        serialNumber: player.serialNumber,
        src,
        isSrcUndefined: src === undefined,
        isBlobUri: typeof src === 'string' && src.includes('blob:'),
        messageId,
      },
    });
    window.clearTimeout(player.reloadTimer);

    // Only transform the main AES manifest.
    // splash / trailer endpoints reject the `Authorization` header (CORS preflight fail).
    const isAesSource =
      this.props.isAesEncrypted && src === this.props.source?.hls;
    const resolveSrc = isAesSource
      ? this.resolveAesSource({ manifestUrl: src, messageId })
      : Promise.resolve(src);

    const loadPromise = resolveSrc
      .then(resolvedSrc => {
        // Building an AES source takes ~350ms, long enough for this short to
        // unmount and for the next one to acquire the very same instance from
        // the pool. Going on would unload what that short just loaded and point
        // the player back at this one's manifest.
        if (this._unmounting || this._player !== player) {
          playerLog({
            messages: [
              `(debugNo: ${debugNo}) loadSource`,
              'skipped: this player is no longer ours',
            ],
            data: { messageId, serialNumber: player.serialNumber },
          });
          return;
        }

        const isBlobUri = resolvedSrc.includes('blob:');
        const isDataUrlHls = resolvedSrc.startsWith(
          'data:application/vnd.apple.mpegurl'
        );
        const mimeHint = isBlobUri
          ? 'video/mp4'
          : isDataUrlHls
            ? 'application/vnd.apple.mpegurl'
            : undefined;
        playerLog({
          messages: [`(debugNo: ${debugNo}) loadSource start`],
          data: {
            pathname: getLogSafePath(resolvedSrc),
            isBlobUri,
            isDataUrlHls,
            mimeHint,
          },
        });
        // A player fresh from the pool was already unloaded on its way back
        // (deactivatePlayer), so unloading again just tears down and rebuilds a
        // MediaSource for nothing — on the critical path of every swipe.
        const unloaded = player.getAssetUri()
          ? player.unload()
          : Promise.resolve();
        // Whatever was loaded before is about to be torn down.
        this._loadedPlayer = undefined;

        return unloaded.then(() =>
          player.load(resolvedSrc, 0, mimeHint).then(() => {
            playerLog({
              messages: [
                `(debugNo: ${debugNo}) playback`,
                `${resolvedSrc} loaded successfully`,
              ],
            });
            this._loadedPlayer = player;
            this.setReloadTimer(player);
          })
        );
      })
      .catch(error => {
        playerLog({
          messages: [`(debugNo: ${debugNo}) loadSource`, 'settled: error'],
          data: {
            serialNumber: player.serialNumber,
            src,
            errorCode: error?.code,
            errorCategory: error?.category,
          },
        });

        // Only capture unexpected errors (7000 is a normal load interruption
        // when the user leaves before the load finishes, not a real failure).
        if (error?.code !== 7000) {
          trackEvent({
            type: EventTypes.PLAYER_ERROR,
            payload: {
              messageId,
              debugNo,
              errorReason: 'load-source-failed',
              errorCode: error?.code ?? null,
              errorCategory: error?.category ?? null,
              errorData: JSON.stringify(error?.data ?? null),
              failedUri: getLogSafePath(src),
              isErrorUndefined: error == null,
              wasUnmounted: !!this._unmounting,
              acquireToSettleMs: this._lastAcquiringTimestamp
                ? Date.now() - this._lastAcquiringTimestamp
                : null,
            },
          });
          playerError(
            `(debugNo: ${debugNo}) playback - failed to load ${src}`,
            error
          );
          this.captureException(error);
          this.deactivatePlayer({
            player,
            debugNo,
            reason: 'an error occurred',
          });
        }
      })
      .finally(() => {
        // Stop shielding this pair once the load has settled, so a genuine
        // reload of the same source later on still goes through. Matched by
        // promise identity, like the AES source above: the pool hands the same
        // instance back out, so comparing (player, src) would let a load from
        // before a release clear the shield the load after it just put up.
        if (this._loadingSource?.promise === loadPromise) {
          this._loadingSource = undefined;
        }
      });

    this._loadingSource = { player, src, promise: loadPromise };
    return loadPromise;
  };

  setReloadTimer = player => {
    const { isNeedReloadCycle, drmLicenseDuration } = this.props;
    if (isNeedReloadCycle) {
      player.reloadTimer = setTimeout(() => {
        if (player.paused()) {
          player.isReloadRequired = true;
        } else {
          this.reload(player);
        }
      }, drmLicenseDuration * 1000);
    }
  };

  componentDidMount() {
    const {
      messageId,
      drmFeatureName,
      isActiveVideo,
      shouldAutoplayPost,
      loadMode,
    } = this.props;
    const debugNo = getDebugNo();
    playerLog({
      messages: [`(debug ${debugNo}) componentDidMount`],
      data: { messageId },
    });
    if (drmFeatureName === DrmFeatureName.SHORT) {
      // A short inside the preload window loads now and parks on its first
      // frame, so the swipe that reaches it only has to call play(). Waiting
      // for it to become active is what made every swipe pay for a full load.
      if (isActiveVideo || loadMode === LoadMode.PRELOAD) {
        this.playback(debugNo);
      } else {
        playerLog({
          messages: [
            `(debug ${debugNo}) componentDidMount`,
            'skip playback (SHORT not active)',
          ],
          data: { messageId, isActiveVideo, loadMode },
        });
      }
    } else if (drmFeatureName === DrmFeatureName.POST) {
      if (shouldAutoplayPost) {
        this.playback(debugNo);
      } else {
        playerLog({
          messages: [
            `(debug ${debugNo}) componentDidMount`,
            'skip playback (POST should not autoplay)',
          ],
          data: { messageId, shouldAutoplayPost },
        });
      }
    } else {
      this.playback(debugNo);
    }
  }

  async componentDidUpdate(prevProps) {
    // [HEADS-UP]
    // Since it takes some time to initializePlayer,
    // `this._player` might not be ready yet as new props are updated!
    const debugNo = this._player?.debugNo || getDebugNo();
    const {
      messageId,
      assetId,
      isDrmLicenseAvailabilityFetched,
      source,
      clientId,
      token,
      canViewMedia,
      isMuted,
      option,
      shouldAutoplayPost,
      isActiveVideo,
      loadMode,
      drmFeatureName,
      enabledImageFullscreenModal,
      isFullscreenModalOpened,
    } = this.props;
    const {
      messageId: prevMessageId,
      assetId: prevAssetId,
      source: prevSource,
      isDrmLicenseAvailabilityFetched: prevIsDrmLicenseAvailabilityFetched,
      clientId: prevClientId,
      token: prevToken,
      canViewMedia: prevCanViewMedia,
      isMuted: prevIsMuted,
      option: prevOption,
      shouldAutoplayPost: prevShouldAutoplayPost,
      isActiveVideo: prevIsActiveVideo,
      isFullscreenModalOpened: prevIsFullscreenModalOpened,
    } = prevProps;
    const isShort = drmFeatureName === DrmFeatureName.SHORT;
    const isPost = drmFeatureName === DrmFeatureName.POST;
    const isModalOpener = isPost && enabledImageFullscreenModal;

    if (
      isPost &&
      shouldAutoplayPost !== prevShouldAutoplayPost &&
      !shouldAutoplayPost
    ) {
      this._isPostInactive = true;
      this.deactivatePlayer({
        player: this._player,
        debugNo,
        reason: 'Post is inactive',
      });
      return;
    }

    if (isShort && isActiveVideo !== prevIsActiveVideo && !isActiveVideo) {
      // Still inside the preload window: park the loaded player at the start
      // instead of tearing it down, so swiping back to this short plays it
      // instantly. Leaving the window unmounts the slide, and
      // componentWillUnmount is what releases the player then.
      if (loadMode === LoadMode.PRELOAD) {
        playerLog({
          messages: [
            `(debug ${debugNo}) componentDidUpdate`,
            'Short left the active slot but stays preloaded',
          ],
          data: { messageId },
        });
        this._player?.pause();
        this._player?.currentTime(0);
        this._player?.configure({
          streaming: { bufferingGoal: PRELOAD_BUFFERING_GOAL_SECONDS },
        });
        // The active slot left `autoplay` on, and it only takes effect at load
        // time: a load still resolving when the swipe happens would start this
        // short off screen once it lands.
        const mediaElement = this._player?.getMediaElement();
        if (mediaElement) {
          mediaElement.autoplay = false;
        }
        return;
      }

      this._isShortInactive = true;
      this.deactivatePlayer({
        player: this._player,
        debugNo,
        reason: 'Short is inactive',
      });
      return;
    }

    if (
      isModalOpener &&
      isFullscreenModalOpened &&
      isFullscreenModalOpened !== prevIsFullscreenModalOpened
    ) {
      this._isPlayerInModalCreated = true;
      this.deactivatePlayer({
        player: this._player,
        debugNo,
        reason: 'Post detail is covered by a newly opened modal',
      });
      return;
    }

    // Keep track of the critical props to determine if we have to load/reload source.
    const diff = Object.create(null);
    if (token !== prevToken) {
      diff.token = token;
      diff.prevToken = prevToken;
    }
    if (clientId !== prevClientId) {
      diff.clientId = clientId;
      diff.prevClientId = prevClientId;
    }
    if (assetId !== prevAssetId) {
      diff.assetId = assetId;
      diff.prevAssetId = prevAssetId;
    }
    if (messageId !== prevMessageId) {
      diff.messageId = messageId;
      diff.prevMessageId = prevMessageId;
    }
    if (canViewMedia !== prevCanViewMedia) {
      diff.canViewMedia = canViewMedia;
      diff.prevCanViewMedia = prevCanViewMedia;
    }
    if (
      isDrmLicenseAvailabilityFetched !== prevIsDrmLicenseAvailabilityFetched
    ) {
      diff.isDrmLicenseAvailabilityFetched = isDrmLicenseAvailabilityFetched;
      diff.prevIsDrmLicenseAvailabilityFetched =
        prevIsDrmLicenseAvailabilityFetched;
    }
    if (shouldAutoplayPost && shouldAutoplayPost !== prevShouldAutoplayPost) {
      diff.shouldAutoplayPost = shouldAutoplayPost;
      diff.prevShouldAutoplayPost = prevShouldAutoplayPost;
    }
    if (isActiveVideo && isActiveVideo !== prevIsActiveVideo) {
      diff.isActiveVideo = isActiveVideo;
      diff.prevIsActiveVideo = prevIsActiveVideo;
    }
    if (
      isModalOpener &&
      isFullscreenModalOpened !== prevIsFullscreenModalOpened
    ) {
      // `isFullscreenModalOpened` here should be false
      // since the truthy case was taken care earlier.
      diff.isFullscreenModalOpened = isFullscreenModalOpened;
      diff.prevIsFullscreenModalOpened = prevIsFullscreenModalOpened;
    }
    if (!isEqual(source, prevSource)) {
      diff.source = source;
      diff.prevSource = prevSource;
    }

    if (Object.keys(diff).length) {
      playerLog({
        messages: [
          `(debug ${debugNo}) componentDidUpdate`,
          'trigger playpack() since props updated',
        ],
        data: { messageId, ...diff },
      });

      // `source` is in `diff`, so a diff holding nothing but the active edge
      // means the preloaded player still carries the right source.
      // `_loadedPlayer` rather than `getAssetUri()`: shaka sets its asset URI
      // early inside load(), so a still-loading preload would look ready.
      const isPreloadedSourceStillValid =
        !!this._player &&
        this._loadedPlayer === this._player &&
        Object.keys(diff).every(key => ACTIVE_EDGE_DIFF_KEYS.includes(key));

      if (isShort && isActiveVideo && isPreloadedSourceStillValid) {
        this.startPreloadedPlayer(debugNo);
      } else if ((!isShort && !isPost) || isActiveVideo || shouldAutoplayPost) {
        // SHORT only plays when it's active (isActiveVideo);
        // POST only plays when it should be playing (shouldAutoplayPost);
        // Others play immediately when critical props updated.
        this.playback(debugNo);
      } else if (isShort && loadMode === LoadMode.PRELOAD) {
        // Waiting its turn inside the preload window — load now so the swipe
        // that reaches it only has to play.
        this.playback(debugNo);
      }
    }

    if (option !== prevOption) {
      playerLog({
        messages: [`(debug ${debugNo}) componentDidUpdate`, 'option updated'],
        data: { option, prevOption },
      });
      for (const [key, value] of Object.entries(option)) {
        const videoElement = this._player?.getMediaElement?.();
        if (videoElement) {
          videoElement[key] = value;
        }
      }
    }
    if (isMuted !== prevIsMuted) {
      playerLog({
        messages: [`(debug ${debugNo}) componentDidUpdate`, 'muted updated'],
        data: { isMuted, prevIsMuted },
      });
      this._player?.muted(isMuted);
      playerPool.updateVideoElementPool({ muted: isMuted });
    }
  }

  componentWillUnmount() {
    const player = this._player;
    if (player) {
      const debugNo = player?.debugNo || '';
      playerLog({
        messages: [`(debug ${debugNo}) componentWillUnmount`],
      });
      this._unmounting = true;
      playerLog({
        messages: [
          `(debug ${debugNo}) componentWillUnmount`,
          'trigger deactivatePlayer()',
        ],
      });
      this.deactivatePlayer({
        player,
        debugNo,
        reason: 'component unmounting',
      });
    }
  }

  render() {
    const { style, children } = this.props;

    return (
      <PlayerContext.Consumer>
        {({ setPlayer }) => {
          this.setPlayer = setPlayer;
          return (
            <StyledShakaPlayer ref={this.videoContainerRef} style={style}>
              {children}
            </StyledShakaPlayer>
          );
        }}
      </PlayerContext.Consumer>
    );
  }
}

ShakaPlayer.propTypes = {
  isDrmLicenseAvailabilityFetched: PropTypes.bool,
  drmFeatureName: PropTypes.string,
  assetId: PropTypes.string,
  clientId: PropTypes.string,
  contentUrl: PropTypes.string,
  token: PropTypes.string,
  sessionId: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
  option: PropTypes.object,
  source: PropTypes.object,
  mediaType: PropTypes.string,
  keySystem: PropTypes.string,
  robustness: PropTypes.string,
  isAesEncrypted: PropTypes.bool,
  canViewMedia: PropTypes.bool,
  isMuted: PropTypes.bool,
  isNeedReloadCycle: PropTypes.bool,
  isActiveVideo: PropTypes.bool, // Shorts
  loadMode: PropTypes.oneOf(Object.values(LoadMode)), // Shorts
  shouldAutoplayPost: PropTypes.bool, // Feed/Post
  shouldCombineTrailer: PropTypes.bool,
  shouldUseTrailer: PropTypes.bool,
  shouldUseSplash: PropTypes.bool,
  isFullscreenModalOpened: PropTypes.bool, // Feed/Post
  enabledImageFullscreenModal: PropTypes.bool, // Feed/Post
  messageId: PropTypes.string,
  splashVideo: PropTypes.object,
  openPlaybackNotSupportAlert: PropTypes.func,
  onLoadedMetadata: PropTypes.func,
  onEnded: PropTypes.func,
  onPause: PropTypes.func,
  onPlay: PropTypes.func,
  onProgressUpdate: PropTypes.func,
  onSetup: PropTypes.func,
  drmLicenseDuration: PropTypes.number,
  widevineServerCertificateBase64: PropTypes.string,
};

ShakaPlayer.defaultProps = {
  isDrmLicenseAvailabilityFetched: false,
  drmFeatureName: '',
  assetId: '',
  clientId: '',
  contentUrl: '',
  token: '',
  sessionId: '',
  style: {},
  children: null,
  option: {},
  source: {},
  mediaType: 'image',
  keySystem: null,
  robustness: null,
  isAesEncrypted: false,
  canViewMedia: false,
  isMuted: true,
  isNeedReloadCycle: false,
  isActiveVideo: false,
  loadMode: LoadMode.NONE,
  shouldAutoplayPost: false,
  shouldCombineTrailer: false,
  shouldUseTrailer: false,
  shouldUseSplash: false,
  isFullscreenModalOpened: false,
  enabledImageFullscreenModal: false,
  messageId: null,
  splashVideo: {},
  openPlaybackNotSupportAlert: () => null,
  onLoadedMetadata: () => null,
  onEnded: () => null,
  onPause: () => null,
  onPlay: () => null,
  onProgressUpdate: null,
  onSetup: () => null,
  drmLicenseDuration: 600,
  widevineServerCertificateBase64: '',
};

const StyledShakaPlayer = styled.div`
  width: 100%;
  height: 100%;

  video {
    width: 100%;
    height: 100%;
  }
`;

export default ShakaPlayer;
